<template>
  <v-container class="modern-prestage-view pb-12">
    <!-- Updated Global Header (Master Pattern) -->
    <v-row class="mb-6 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="44" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white" small>fas fa-magic</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text d-flex align-center lh-1">
            Gestión de Pre-Producción
          </div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Control de diseño, validación y flujos técnicos</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center gap-3">
        <export-prestage :disabled="loading" :filter="filter" :options="options" />
        <export-prestage-products :filter="filter" :options="options" />
      </div>
    </v-row>

    <!-- Unified Filter Card (No sheet header for Master views) -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row align="end">
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Fecha OT Desde</div>
          <v-text-field 
            v-model="filter.created_after" 
            outlined dense hide-details="auto"
            type="date"
            class="modern-input bg-white"
            @change="triggerFetch"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Fecha OT Hasta</div>
          <v-text-field 
            v-model="filter.created_before" 
            outlined dense hide-details="auto"
            type="date"
            class="modern-input bg-white"
            @change="triggerFetch"
          />
        </v-col>
        <v-col cols="12" md="6">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-2 ml-1">Etapa del Flujo activo</div>
          <v-btn-toggle mandatory v-model="filter.stage" @change="fetch" class="modern-toggle d-flex flex-wrap">
            <v-btn small :value="-2" class="px-4 font-weight-black">Todos</v-btn>
            <v-btn
              small
              v-for="(stage, index) in stages"
              :key="index"
              :value="stage.id"
              class="px-4 font-weight-bold"
              active-class="active-toggle-item"
            >
              <v-icon x-small left :color="stage.color || 'grey'" v-if="stage.id !== -2">fas fa-circle</v-icon>
              {{ stage.name }}
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Results Section -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        show-select
        item-key="id"
        class="modern-table"
        @click:row="showProduct"
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 25, 50, 100],
        }"
      >
        <!-- Search Bar in Top Slot (Master Pattern) -->
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom d-flex align-center bg-white">
            <v-text-field
              v-model="search"
              placeholder="Buscar por OT, SKU, Producto o Cliente..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details="auto"
              class="modern-search-field flex-grow-1 mr-4"
              @keyup="searchTimeOut"
            />
            
            <transition name="fade">
              <v-btn
                v-if="selected.length > 0"
                color="secondary"
                depressed
                @click.stop="openBatchChange"
                class="px-6 rounded-lg font-weight-black text-uppercase tracking-wider shadow-md scale-in"
              >
                <v-icon small class="mr-2">fas fa-layer-group</v-icon>
                Cambio Masivo ({{ selected.length }})
              </v-btn>
            </transition>
          </div>
        </template>

        <!-- OT Reference -->
        <template v-slot:item.work_order__id="{ item }">
          <div class="d-flex align-center pointer ot-tag" @click.stop="showWorkOrder({ id: item.work_order__id })">
            <v-avatar size="28" color="primary" class="mr-2 shadow-xs">
              <span class="caption font-weight-black white--text">OT</span>
            </v-avatar>
            <span class="body-2 font-weight-black primary--text tracking-tighter">{{ item.work_order__id }}</span>
          </div>
        </template>

        <!-- Product Details -->
        <template v-slot:item.library_product__sku="{ item }">
          <div class="py-2">
            <div class="caption font-weight-black grey--text text-uppercase tracking-wider mb-1">{{ item.library_product__sku }}</div>
            <div class="body-2 font-weight-black grey--text text--darken-4 text-truncate" style="max-width: 320px;">{{ item.name }}</div>
          </div>
        </template>

        <!-- Client Info -->
        <template v-slot:item.work_order__sales_note__customer__name="{ item }">
          <div class="body-2 font-weight-bold secondary--text tracking-tight lh-1-2">
            <v-icon x-small color="secondary" class="mr-1 opacity-60">fas fa-user</v-icon>
            {{ item.work_order__sales_note__customer__name }}
          </div>
        </template>

        <!-- Important Dates -->
        <template v-slot:item.work_order__created_at="{ item }">
          <div class="d-flex flex-column">
            <span class="caption grey--text font-weight-bold">{{ item.work_order__created_at | moment("DD/MM/YYYY") }}</span>
          </div>
        </template>

        <template v-slot:item.work_order__delivery_date="{ item }">
          <v-chip dark small color="primary" class="font-weight-black shadow-xs tracking-wider">
            {{ item.work_order__delivery_date | moment("DD/MM/YYYY") }}
          </v-chip>
        </template>

        <!-- Status / Stage -->
        <template v-slot:item.prestage="{ item }">
          <v-tooltip bottom max-width="300" open-delay="300" content-class="premium-tooltip">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="d-inline-block">
                <v-chip
                  v-if="item.prestage && item.in_prestage"
                  :color="item.prestage.color || 'primary'"
                  label
                  outlined
                  small
                  class="font-weight-black shadow-xs px-4 status-pill"
                >
                  <v-icon x-small left :color="item.prestage.color">fas fa-dot-circle</v-icon>
                  {{ item.prestage.name.toUpperCase() }}
                </v-chip>
                <v-chip
                  v-else
                  label
                  dark
                  color="success"
                  small
                  class="font-weight-black px-4 shadow-xs status-pill"
                >
                  <v-icon x-small left color="white">fas fa-industry</v-icon>
                  PRODUCCIÓN
                </v-chip>
              </div>
            </template>
            <div class="pa-2" v-if="item.pre_stage_logs && item.pre_stage_logs.length > 0">
              <div class="caption font-weight-black mb-1 text-uppercase tracking-widest text-center" style="border-bottom: 1px solid rgba(255,255,255,0.2); padding-bottom: 4px;">Última Trazabilidad</div>
              <div class="d-flex align-center mt-2">
                <v-icon x-small color="white" class="mr-2">fas fa-user-edit</v-icon>
                <div class="caption font-weight-bold">{{ item.pre_stage_logs[0].email }}</div>
              </div>
              <div class="d-flex align-center mt-1">
                <v-icon x-small color="white" class="mr-2">fas fa-clock</v-icon>
                <div class="caption opacity-80">{{ item.pre_stage_logs[0].date | moment("DD/MM/YYYY HH:mm") }}</div>
              </div>
            </div>
          </v-tooltip>
        </template>

        <!-- Row Actions -->
        <template v-slot:item.actions="{ item }">
          <v-menu offset-y left rounded="xl" transition="slide-y-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" class="modern-action-btn">
                <v-icon small color="grey darken-1">fas fa-ellipsis-h</v-icon>
              </v-btn>
            </template>
            <v-list dense class="pa-1 modern-list">
              <v-list-item v-if="item.in_prestage" @click.stop="openRowStageChange(item)" class="rounded-lg mb-1">
                <v-list-item-icon class="mr-3"><v-icon small color="primary">fas fa-exchange-alt</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-black grey--text text--darken-3">Cambiar Etapa</v-list-item-title>
              </v-list-item>
              
              <v-list-item v-if="item.in_prestage" @click.stop="confirmToProduction(item)" class="rounded-lg mb-1 item-success">
                <v-list-item-icon class="mr-3"><v-icon small color="success">fas fa-industry</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-black success--text">Pasar a Producción</v-list-item-title>
              </v-list-item>
              
              <v-divider v-if="item.in_prestage" class="my-1"></v-divider>
              
              <v-list-item @click.stop="printWorkOrder(item)" class="rounded-lg">
                <v-list-item-icon class="mr-3"><v-icon small color="blue-grey lighten-1">fas fa-print</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Imprimir OT</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modals Section -->
    <!-- View OT Details -->
    <v-dialog v-model="display" max-width="1200" scrollable persistent transition="dialog-bottom-transition">
      <v-card outlined class="modern-card overflow-hidden elevation-24">
        <work-order-card 
          :work_order="workOrderOnDisplay"
          @close="display = false" 
          @refresh="fetch"
          :loading="workOrderOnDisplayLoading"
        /> 
      </v-card>
    </v-dialog>

    <!-- Change Prestage Stages -->
    <v-dialog v-model="showChangePrestage" persistent max-width="700" transition="dialog-fade-transition">
      <v-card outlined class="modern-card overflow-hidden elevation-24">
        <v-sheet color="primary" class="pa-6 d-flex align-center shadow-sm">
          <v-avatar color="primary lighten-1" size="32" class="mr-4">
            <v-icon small color="white">fas fa-sliders-h</v-icon>
          </v-avatar>
          <span class="text-h6 white--text font-weight-black text-uppercase tracking-widest">Ajuste de Flujo Pre-Prensa</span>
          <v-spacer></v-spacer>
          <v-btn icon color="white" small @click="showChangePrestage = false" class="grey lighten-1 opacity-20">
            <v-icon x-small>fas fa-times</v-icon>
          </v-btn>
        </v-sheet>
        <v-card-text class="pa-0">
          <change-prestage 
            :products="selected.length > 0 ? selected : [productToChangePrestage]"
            @close="showChangePrestage = false"
            @updated="() => { selected = []; fetch(); showChangePrestage = false; }"
          /> 
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Product History / Detail -->
    <v-dialog v-model="showProductModal" max-width="800" transition="dialog-bottom-transition">
      <v-card outlined class="modern-card overflow-hidden elevation-24">
        <v-sheet color="secondary" class="pa-6 d-flex align-center shadow-sm">
          <v-avatar color="secondary lighten-1" size="32" class="mr-4">
            <v-icon small color="white">fas fa-history</v-icon>
          </v-avatar>
          <span class="text-h6 white--text font-weight-black text-uppercase tracking-widest">Trazabilidad Técnica</span>
          <v-spacer></v-spacer>
          <v-btn icon color="white" small @click="showProductModal = false" class="grey lighten-1 opacity-20">
            <v-icon x-small>fas fa-times</v-icon>
          </v-btn>
        </v-sheet>
        <v-card-text class="pa-0">
          <detail-prestages :product="productOnDisplay" />
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-6 bg-grey-light justify-end">
          <v-btn depressed color="secondary" class="rounded-lg font-weight-bold px-8" @click="showProductModal = false">Entendido</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <confirm
      v-if="productToProduction"
      v-model="showProductToProduction" 
      :message="`¿Confirma pasar a Producción el producto [${productToProduction.library_product__sku}] de la OT #${productToProduction.work_order__id}? Toda etapa de pre-prensa se dará por finalizada.`"
      @ok="changeToProduction(productToProduction)"
      @cancel="() => { showProductToProduction = false; productToProduction = null; }"
    />
  </v-container>
</template>

<script>
import { fetchProductionProducts, getWorkOrder, updateProductionProduct } from '@/api/production'
import notifications from '@/utils/notifications'
import WorkOrderCard from '@/components/production/WorkOrderCard'
import DetailPrestages from '@/components/production/DetailPrestages'
import ChangePrestage from '@/components/production/ChangePrestage'
import ExportPrestageProducts from '@/components/production/ExportPrestageProducts'
import ExportPrestage from '@/components/production/ExportPrestage.vue'
import { pdfWorkOrder } from '@/doc_generators/workOrder'

export default {
  name: 'ProductsInPrestage',
  components: { WorkOrderCard, ExportPrestageProducts, ChangePrestage, DetailPrestages, ExportPrestage },
  data() {
    return {
      loading: false,
      stages: [],
      filter: { created_after: '', created_before: '', stage: -2 },
      options: { sortBy: ['work_order__created_at'], sortDesc: [true], page: 1, itemsPerPage: 10 },
      totalItems: 0,
      display: false,
      showChangePrestage: false,
      productToChangePrestage: null,
      workOrderOnDisplay: {},
      workOrderOnDisplayLoading: false,
      search: '',
      items: [],
      selected: [],
      showProductModal: false,
      productOnDisplay: { pre_stage_logs: [] },
      productToProduction: null,
      showProductToProduction: false,
      headers: [
        { text: 'OT', align: 'start', value: 'work_order__id', class: 'grey lighten-4', width: '100' },
        { text: 'Referencia / Producto', align: 'start', value: 'library_product__sku', class: 'grey lighten-4' },
        { text: 'Cliente', value: 'work_order__sales_note__customer__name', class: 'grey lighten-4' },
        { text: 'Ingreso', align: 'start', value: 'work_order__created_at', class: 'grey lighten-4', width: '110' },
        { text: 'Compromiso', align: 'start', value: 'work_order__delivery_date', class: 'grey lighten-4', width: '110' },
        { text: 'Etapa Actual', value: 'prestage', align: 'center', sortable: false, class: 'grey lighten-4', width: '160' },
        { text: 'Acciones', value: 'actions', align: 'center', sortable: false, class: 'grey lighten-4', width: '80' }
      ],
      timer: null
    }
  },
  methods: {
    triggerFetch() {
      this.options.page = 1
      this.fetch()
    },
    async fetch() {
      this.loading = true
      let in_prestage = ''
      let stage = this.filter.stage
      if (stage === -2) {
        in_prestage = ''
        stage = ''
      } else if (stage === -1) {
        in_prestage = false
        stage = ''
      } else {
        in_prestage = true
      }

      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      const ordering = sortBy.length ? (sortDesc[0] ? '-' : '') + sortBy[0] : '-work_order__created_at'
      const query = `search=${this.search}&ordering=${ordering}&in_prestage=${in_prestage}&page=${page}&page_size=${itemsPerPage}&created_after=${this.filter.created_after}&created_before=${this.filter.created_before}&prestage__id=${stage}`
      
      try {
        const data = await fetchProductionProducts(query)
        this.totalItems = data.count
        this.items = data.results
      } catch (err) {
        notifications.showError('Error cargando lista: ' + err)
      } finally {
        this.loading = false
      }
    },
    showProduct(item) { 
      this.showProductModal = true
      this.productOnDisplay = item
    },
    async showWorkOrder(item) {
      this.workOrderOnDisplayLoading = true
      this.display = true
      try {
        this.workOrderOnDisplay = await getWorkOrder(item.id)
      } catch (e) {
        notifications.showError('Error al cargar OT: ' + e)
        this.display = false
      } finally {
        this.workOrderOnDisplayLoading = false
      }
    },
    searchTimeOut() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => this.fetch(), 600)
    },
    openBatchChange() {
      this.showChangePrestage = true
    },
    openRowStageChange(item) {
      this.productToChangePrestage = item
      this.selected = [] 
      this.showChangePrestage = true
    },
    confirmToProduction(item) {
      this.productToProduction = item
      this.showProductToProduction = true
    },
    async changeToProduction(item) {
      this.showProductToProduction = false
      this.loading = true
      try {
        await updateProductionProduct(item.id, { in_prestage: false })
        notifications.showSuccess('Producto movido a Producción')
        this.fetch()
      } catch (e) {
        notifications.showError('Error: ' + e)
      } finally {
        this.loading = false
        this.productToProduction = null
      }
    },
    printWorkOrder(item) {
      getWorkOrder(item.work_order__id).then(data => {
        pdfWorkOrder(data, this.$store.getters.getUser)
      })
    }
  },
  async mounted() {
    this.stages = [...this.$store.getters.getStages, { id: -1, name: 'Producción', color: 'success' }]
    this.fetch()
  }
}
</script>

<style scoped>
.modern-prestage-view { max-width: 1400px; margin: 0 auto; }
.modern-card { border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 12px !important; background-color: #fff; }
.modern-toggle { background: #fff; border-radius: 12px; padding: 4px; border: 1px solid rgba(0,0,0,0.08); gap: 4px; }
.modern-toggle .v-btn { border: none !important; border-radius: 8px !important; text-transform: none; font-weight: 700; background: transparent !important; height: 32px !important; }
.active-toggle-item { background: var(--v-primary-base) !important; color: white !important; box-shadow: 0 4px 8px rgba(var(--v-primary-base), 0.2) !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.bg-grey-light { background-color: #f8f9fa; }
.shadow-md { box-shadow: 0 4px 12px rgba(var(--v-primary-base), 0.2) !important; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }
.shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.03) !important; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.gap-4 { gap: 16px; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2; }
.pointer { cursor: pointer; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 2px; }
.tracking-wider { letter-spacing: 1px; }
.tracking-tight { letter-spacing: -0.2px; }
.tracking-tighter { letter-spacing: -0.5px; }
.opacity-20 { opacity: 0.2; }
.opacity-60 { opacity: 0.6; }
.opacity-70 { opacity: 0.7; }
.opacity-80 { opacity: 0.8; }
.opacity-90 { opacity: 0.9; }

.modern-table >>> .v-data-table-header th { white-space: nowrap; height: 56px !important; text-transform: uppercase; font-size: 0.7rem !important; letter-spacing: 1.5px; font-weight: 900 !important; }
.modern-table >>> td { padding: 12px 16px !important; }
.modern-table >>> tr:hover { background-color: rgba(var(--v-primary-base), 0.02) !important; }

.ot-tag { transition: transform 0.2s; }
.ot-tag:hover { transform: scale(1.05); }

.status-pill { min-width: 140px; justify-content: center; border-width: 1.5px !important; }

.modern-action-btn { background-color: #f8f9fa !important; border: 1px solid #eee !important; transition: all 0.2s; }
.modern-action-btn:hover { background-color: #fff !important; border-color: var(--v-primary-base) !important; color: var(--v-primary-base) !important; }

.modern-list .v-list-item:hover { background-color: rgba(var(--v-primary-base), 0.05); }
.item-success:hover { background-color: rgba(var(--v-success-base), 0.05) !important; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.scale-in { animation: scaleIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
@keyframes scaleIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }

.premium-tooltip { background: rgba(33, 33, 33, 0.95) !important; backdrop-filter: blur(4px); border-radius: 12px !important; border: 1px solid rgba(255,255,255,0.1); }
</style>
